import type { InputSource } from './types';

function destroyVideo(userData: Record<string, any>) {
  if (userData._videoRaf) {
    cancelAnimationFrame(userData._videoRaf);
    userData._videoRaf = null;
  }
  if (userData._videoEl) {
    userData._videoEl.pause();
    userData._videoEl = null;
    userData._videoSrc = null;
  }
}

export const source: InputSource = {
  label: 'Source',
  value: 'source',
  draw: (ctx, width, height, userData) => {
    const drawLoading = () => {
      ctx.fillStyle = '#222';
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = '#fff';
      ctx.font = `${Math.floor(Math.min(width, height) * 0.08)}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Loading...', width / 2, height / 2);
    };

    if (userData?.sourceUrl && userData?.sourceAssetType) {
      const { sourceUrl, sourceAssetType: type } = userData;

      // ── Image ──────────────────────────────────────────────────────────
      if (type === 'IM') {
        destroyVideo(userData);

        if (userData._imgSrc !== sourceUrl) {
          userData._imgSrc = sourceUrl;
          const img = new window.Image();
          img.crossOrigin = 'anonymous';
          drawLoading();
          img.onload = () => {
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
            if (userData.texture) userData.texture.needsUpdate = true;
            userData.render?.();
          };
          img.onerror = () => {
            ctx.fillStyle = '#a00';
            ctx.fillRect(0, 0, width, height);
            ctx.fillStyle = '#fff';
            ctx.font = `${Math.floor(Math.min(width, height) * 0.08)}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('Failed to load image', width / 2, height / 2);
            userData.render?.();
          };
          img.src = sourceUrl;
        }
        return;
      }
      if (type === 'VI') {
        if (userData._videoSrc && userData._videoSrc !== sourceUrl) {
          destroyVideo(userData);
        }

        if (!userData._videoEl) {
          userData._videoSrc = sourceUrl;

          const video = userData.textureManager?.getVideoElement(sourceUrl);
          if (!video) return;

          video.muted = true;
          video.playsInline = true;
          video.loop = false;

          drawLoading();

          // Track state for initialization and looping
          let wasInRange = false;
          let videoInitialized = false;

          const drawFrame = () => {
            userData._videoRaf = requestAnimationFrame(drawFrame);

            const timelinePlayer = userData.timelinePlayer;
            if (!timelinePlayer) return;

            const currentTime = timelinePlayer.currentTime;
            const mediaStartTime = userData.mediaStartTime ?? 0;
            const mediaStart = userData.mediaStart ?? 0;
            const mediaDuration = userData.mediaDuration ?? 10;
            const mediaEndTime = mediaStartTime + mediaDuration;
            const isInRange = currentTime >= mediaStartTime && currentTime <= mediaEndTime;

            // Initialize video position when entering the media block range
            if (isInRange && !wasInRange && !videoInitialized) {
              videoInitialized = true;
              if (video.readyState >= 2) {
                video.currentTime = mediaStart;
              }
            }

            // Reset initialization flag when exiting range so it can reinit on next entry
            if (!isInRange && wasInRange) {
              videoInitialized = false;
            }

            // Handle play/pause based on timeline state and block range
            if (timelinePlayer.isPlaying && isInRange) {
              if (video.paused) {
                video.play().catch(() => {});
              }
              // Check if video reached its end while playing (natural loop)
              if (video.duration && video.currentTime >= video.duration - 0.1) {
                video.currentTime = mediaStart;
              }
            } else {
              if (!video.paused) {
                video.pause();
              }
            }

            // Draw frame to canvas
            if (video.readyState >= video.HAVE_CURRENT_DATA && isInRange) {
              ctx.clearRect(0, 0, width, height);
              ctx.drawImage(video, 0, 0, width, height);
              if (userData.texture) userData.texture.needsUpdate = true;
              userData.render?.();
            } else if (!isInRange) {
              ctx.fillStyle = '#000';
              ctx.fillRect(0, 0, width, height);
              if (userData.texture) userData.texture.needsUpdate = true;
              userData.render?.();
            }

            wasInRange = isInRange;
          };

          video.addEventListener(
            'canplay',
            () => {
              if (!userData._videoRaf) drawFrame();
            },
            { once: true }
          );

          if (video.readyState >= video.HAVE_CURRENT_DATA) {
            drawFrame();
          }

          video.onerror = () => {
            ctx.fillStyle = '#a00';
            ctx.fillRect(0, 0, width, height);
            ctx.fillStyle = '#fff';
            ctx.font = `${Math.floor(Math.min(width, height) * 0.08)}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('Failed to load video', width / 2, height / 2);
            userData.render?.();
          };

          userData._videoEl = video;
        }
        return;
      }
    }

    // ── Fallback placeholder ───────────────────────────────────────────
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, width, height);

    const cx = width / 2;
    const cy = height / 2;
    const size = Math.min(width, height) * 0.2;

    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.beginPath();
    ctx.moveTo(cx - size * 0.4, cy - size * 0.5);
    ctx.lineTo(cx + size * 0.5, cy);
    ctx.lineTo(cx - size * 0.4, cy + size * 0.5);
    ctx.closePath();
    ctx.fill();
  },
  settings: [{ label: 'Source', key: 'source', type: 'asset' }],
};
